import { useState, useEffect, useMemo, useCallback } from 'react';
import { usePersistentState } from '@hooks/usePersistentState';
import { useCxCompanies, useCxJourneys, useCxJourney, useCxSettings, useCxMutations } from '@hooks/useCallExperience';
import { parseApiError } from '@utils/errorUtils';
import ViewingPicker from './ViewingPicker';
import JourneyView from './JourneyView';
import ContentDrawer from './ContentDrawer';
import ContentLibrary from './ContentLibrary';
import SettingsSummary from './SettingsSummary';
import TestCallPanel from './TestCallPanel';
import PatientPicker from './PatientPicker';
import CallAtAGlance from './CallAtAGlance';
import { journeyKey, JOURNEY_ICONS, CxPrefs } from './cxFormat';
import './CallExperience.css';

const PRODUCT_LINE_NAMES = { 1: 'CPAP', 2: 'CGM' };
const LIBRARY = 'library';
const SETTINGS = 'settings';

/** One-line status of a journey for the viewing context, from the company's settings. */
function journeyStatus(journey, settings) {
  if (!settings) return null;
  if (journey.id === 'inbound') return { dot: 'g', text: settings.teamName ? `Queue: ${settings.teamName}` : 'No team' };
  if (journey.id === 'outbound-coach') return { dot: 'g', text: settings.recordsCalls ? 'Recorded' : 'Not recorded' };
  const line = settings.productLines.find((p) => p.id === journey.productLineId);
  if (!line?.active) return { dot: 'n', text: `No ${PRODUCT_LINE_NAMES[journey.productLineId]} product line` };
  if (!settings.ivr.enabled) return { dot: 'r', text: 'IVR off' };
  if (settings.ivr.paused) return { dot: 'y', text: 'IVR paused' };
  return { dot: 'g', text: 'IVR on' };
}

function ContextBar({ ctx, company, onSelect }) {
  if (!ctx.companyId) {
    return (
      <div className="cx-ctxbar cx-ctxbar-global">
        🌐 <span><b>Global default</b>: what every company hears unless it has its own wording. Click a line to edit it and see which companies customize it.</span>
      </div>
    );
  }
  if (ctx.isParent) {
    return (
      <div className="cx-ctxbar">
        ⬡ <span><b>{ctx.name}</b>: parent group of {company?.memberCount ?? 0} companies. Messages set here apply to every member unless that member has its own. IVR questions can&apos;t be set for a group.</span>
        <span className="cx-spacer" />
        <button type="button" className="cx-btn cx-btn-small" onClick={() => onSelect('')}>Back to global default</button>
      </div>
    );
  }
  return (
    <div className="cx-ctxbar">
      🏢 <span>
        <b>{ctx.name}</b> ({ctx.companyId})
        {company?.parentId && <> · member of <button type="button" className="cx-link" onClick={() => onSelect(String(company.parentId))}>{company.parentName}</button></>}
        {' '}· {company?.ownWordingCount ?? 0} custom wording · {company?.offCount ?? 0} turned off. Everything else is inherited.
      </span>
      <span className="cx-spacer" />
      <button type="button" className="cx-btn cx-btn-small" onClick={() => onSelect('')}>Back to global default</button>
    </div>
  );
}

/** Preview the resupply call as one of the company's patients: their insurance and record decide what's skipped. */
function PreviewBar({ journey, ctx, patient, onSelect }) {
  if (journey.id !== 'resupply-ivr' || !ctx.isCompany) return null;
  const preview = journey.preview;
  return (
    <div className="cx-preview-bar">
      <div className="cx-row">
        <b>👤 Preview as a patient</b>
        {!patient && <span className="cx-muted cx-small">See which steps this patient&apos;s call skips, and the supplies they&apos;d be offered.</span>}
      </div>
      <PatientPicker companyId={ctx.companyId} selected={patient} onSelect={onSelect} productLineId={journey.productLineId} autoFocus={false} />
      {patient && preview && (
        <div className="cx-callout cx-mt">
          Previewing <b>{preview.name}</b>{preview.primaryInsuranceName ? <> · primary insurance <b>{preview.primaryInsuranceName}</b></> : ' · no primary insurance'}
          {' '}· eligible for {preview.eligibleItems.length ? preview.eligibleItems.join(', ') : 'nothing right now'}.
          <div className="cx-muted cx-small">Preview only: nothing is called or saved. The patient&apos;s answers and checks made during the call (like the phone&apos;s line type) can still change the path.</div>
        </div>
      )}
    </div>
  );
}

function JourneyCallouts({ journey, ctx, settings }) {
  if (journey.id === 'inbound') {
    return <div className="cx-callout">The inbound IVR isn&apos;t built in Phoenix_PhoneSystem yet. IVR-number calls and the queue&apos;s &quot;press 1&quot; hand off to the legacy IVR in Phoenix_Back_End.</div>;
  }
  if (journey.id === 'resupply-ivr' && journey.productLineId === 2) {
    const noLine = ctx.isCompany && settings && !settings.productLines.some((p) => p.id === 2 && p.active);
    return (
      <div className={`cx-callout${noLine ? ' cx-callout-warn' : ''}`}>
        CGM calls go to patients on CGM only, at companies with an active CGM product line. Automated outreach
        runs for the companies the Phoenix_Automation job starts it for; a call can also be started by hand from Test call.
        {noLine && <> {ctx.name} has no active CGM product line, so it gets no CGM calls.</>}
      </div>
    );
  }
  return null;
}

/**
 * Call Experience: every call journey as the script a caller hears, for the global default, a parent
 * group or a company, with each line editable in context. Replaces the Voice Templates, IVR Questions
 * and IVR Flow tabs. `phone` is the dashboard's browser phone (device state and call callbacks), used by
 * Test call to dial out.
 */
export default function CallExperienceTab({ phone }) {
  const [companyId, setCompanyId] = usePersistentState('cx.company', '');
  const [view, setView] = usePersistentState('cx.view', 'inbound:');
  const [lang, setLang] = usePersistentState('cx.lang', 'en');
  const [drawer, setDrawer] = useState(null);
  const [jumpTo, setJumpTo] = useState(null);
  const clearJump = useCallback(() => setJumpTo(null), []);
  const [testing, setTesting] = usePersistentState('cx.testing', false);
  // In memory only: a patient choice doesn't survive a page reload.
  const [previewPatient, setPreviewPatient] = usePersistentState('cx.previewPatient', null);
  const [technical, setTechnical] = usePersistentState('cx.technical', false, { storage: 'local' });
  const [hear, setHear] = usePersistentState('cx.hear', false, { storage: 'local' });
  const prefs = useMemo(() => ({ technical, hear }), [technical, hear]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { data: companies = [], isLoading: companiesLoading } = useCxCompanies();
  const { data: journeys = [] } = useCxJourneys();
  const isJourneyView = view !== LIBRARY && view !== SETTINGS;
  const current = isJourneyView ? (journeys.find((j) => journeyKey(j) === view) || journeys[0]) : null;
  const previewGuid = current?.id === 'resupply-ivr' && previewPatient?.companyId === String(companyId) ? previewPatient.guid : null;
  const { data: journey, isLoading: journeyLoading, error: journeyError, isFetching } = useCxJourney(
    current?.id, current?.productLineId, companyId, lang, previewGuid);
  const { toggle } = useCxMutations();

  const company = companyId ? companies.find((c) => String(c.id) === String(companyId)) : null;
  const ctx = useMemo(() => ({
    companyId: company ? String(company.id) : '',
    isParent: !!company?.isParent,
    isCompany: !!company && !company.isParent,
    name: company ? company.name : 'Global default',
  }), [company]);
  const { data: settings } = useCxSettings(ctx.isCompany ? ctx.companyId : '');

  useEffect(() => {
    if (!error && !success) return undefined;
    const t = setTimeout(() => { setError(''); setSuccess(''); }, 3000);
    return () => clearTimeout(t);
  }, [error, success]);

  const notify = useCallback((type, message) => {
    if (type === 'error') { setError(message); setSuccess(''); } else { setSuccess(message); setError(''); }
  }, []);

  const onToggle = async (identifier, enabled) => {
    try {
      await toggle.mutateAsync({ identifier, companyId: ctx.companyId, enabled });
      notify('success', `Turned ${enabled ? 'on' : 'off'} for ${ctx.name}`);
    } catch (err) {
      notify('error', parseApiError(err));
    }
  };

  // The drawer reads the line from the current journey so badges and switches stay live after edits.
  const liveLine = drawer && (journey?.steps.flatMap((s) => s.prompts)
    .find((p) => p.kind === drawer.line.kind && p.key === drawer.line.key) || drawer.line);

  const switchContext = (id, options = {}) => {
    setCompanyId(id);
    setDrawer((d) => (d ? { ...d, startEditing: !!options.editing } : d));
  };

  return (
    <CxPrefs.Provider value={prefs}>
    <div className="cx-tab">
      <header className="cx-top">
        <h2>Call Experience</h2>
        <div className="cx-field">Viewing
          <ViewingPicker companies={companies} value={companyId} onSelect={(id) => { setCompanyId(id); setDrawer(null); }} loading={companiesLoading} />
        </div>
        <div className="cx-field">Language
          <div className="cx-seg" role="group" aria-label="Language">
            <button type="button" className={lang === 'en' ? 'cx-on' : ''} aria-pressed={lang === 'en'} onClick={() => setLang('en')}>English</button>
            <button type="button" className={lang === 'es' ? 'cx-on' : ''} aria-pressed={lang === 'es'} onClick={() => setLang('es')}>Español</button>
          </div>
        </div>
        <label className="cx-field cx-small" title="Fill in names, supplies and other placeholders the way a caller hears them">
          <input type="checkbox" checked={hear} onChange={(e) => setHear(e.target.checked)} /> Hear it
        </label>
        <label className="cx-field cx-small" title="Show internal keys, database columns and option names">
          <input type="checkbox" checked={technical} onChange={(e) => setTechnical(e.target.checked)} /> Show technical details
        </label>
        {isFetching && <span className="cx-muted cx-small">Refreshing…</span>}
      </header>

      {error && <div className="cx-error" role="alert">{error}</div>}
      {success && <div className="cx-success" role="status">{success}</div>}

      <div className={`cx-layout${testing && isJourneyView && journey ? ' cx-layout-testing' : ''}`}>
        <nav className="cx-side" aria-label="Journeys">
          <h4>Journeys</h4>
          {journeys.map((j) => {
            const status = journeyStatus(j, settings);
            const key = journeyKey(j);
            return (
              <button key={key} type="button" className={`cx-item${key === journeyKey(current || {}) ? ' cx-item-on' : ''}`}
                onClick={() => { setView(key); setDrawer(null); }} aria-current={key === view ? 'page' : undefined}>
                {JOURNEY_ICONS[j.id] || '📞'}&nbsp; {j.title}
                {status && <span className="cx-item-sub"><span className={`cx-dot cx-dot-${status.dot}`} />{status.text}</span>}
              </button>
            );
          })}
          <h4>Manage</h4>
          <button type="button" className={`cx-item${view === LIBRARY ? ' cx-item-on' : ''}`} onClick={() => { setView(LIBRARY); setDrawer(null); }}>
            📚&nbsp; All content<span className="cx-item-sub">Every spoken line</span>
          </button>
          <button type="button" className={`cx-item${view === SETTINGS ? ' cx-item-on' : ''}`} onClick={() => { setView(SETTINGS); setDrawer(null); }}>
            ⚙&nbsp; Settings summary<span className="cx-item-sub">{ctx.companyId ? 'What changes the path' : 'Global options & flags'}</span>
          </button>
        </nav>

        <main className="cx-main">
          <ContextBar ctx={ctx} company={company} onSelect={(id) => { setCompanyId(id); setDrawer(null); }} />
          {view === LIBRARY && <ContentLibrary ctx={ctx} lang={lang} journeys={journeys} onOpen={(line) => setDrawer({ line, startEditing: false })} />}
          {view === SETTINGS && (
            <SettingsSummary ctx={ctx} onSelect={(id) => { setCompanyId(id); setDrawer(null); }} onToggle={onToggle} togglePending={toggle.isPending} />
          )}
          {isJourneyView && journeyError && <div className="cx-error">{parseApiError(journeyError)}</div>}
          {isJourneyView && journeyLoading && !journey && <div className="cx-muted">Loading journey…</div>}
          {isJourneyView && journey && (
            <>
              <div className="cx-jhead">
                <h2 className="cx-jtitle">{JOURNEY_ICONS[journey.id] || '📞'} {journey.title}</h2>
                <span className="cx-spacer" />
                <button type="button" className="cx-btn cx-btn-primary" onClick={() => setTesting(!testing)} aria-pressed={testing}>{testing ? 'Hide test call' : '▶ Test call'}</button>
              </div>
              <JourneyCallouts journey={journey} ctx={ctx} settings={settings} />
              <PreviewBar journey={journey} ctx={ctx} patient={previewGuid ? previewPatient : null}
                onSelect={(p) => setPreviewPatient(p ? { ...p, companyId: ctx.companyId } : null)} />
              <CallAtAGlance journey={journey} ctx={ctx} onJump={(id) => setJumpTo({ id, at: Date.now() })} />
              <JourneyView
                journey={journey}
                jumpTo={jumpTo}
                onJumped={clearJump}
                ctx={ctx}
                lang={lang}
                onOpen={(line) => setDrawer({ line, startEditing: false })}
                onToggle={onToggle}
                togglePending={toggle.isPending}
              />
            </>
          )}
        </main>

        {testing && isJourneyView && journey && (
          <TestCallPanel key={journeyKey(journey)} journey={journey} ctx={ctx} settings={settings} phone={phone || {}}
            onClose={() => setTesting(false)} />
        )}
      </div>


      {drawer && (
        <ContentDrawer
          key={`${drawer.line.kind}:${drawer.line.key}:${ctx.companyId}:${drawer.startEditing}`}
          line={liveLine}
          ctx={ctx}
          lang={lang}
          companies={companies}
          startEditing={drawer.startEditing}
          onClose={() => setDrawer(null)}
          onSwitchContext={switchContext}
          onGoToJourney={(u) => { setView(`${u.journeyId}:${u.productLineId ?? ''}`); setDrawer(null); }}
          onNotify={notify}
        />
      )}
    </div>
    </CxPrefs.Provider>
  );
}
